import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { AvatarFrame } from './AvatarFrame';
import { TitleBadge } from './TitleBadge';
import type { FrameType } from './AvatarFrame';
import type { TitleType } from './TitleBadge';
import type {
    UserTitle,
    AvatarFrame as AvatarFrameType,
    LevelInfo,
} from '../../services/progressionService';
import * as progressionService from '../../services/progressionService';
import './CustomizationSheet.css';

interface CustomizationSheetProps {
    isOpen: boolean;
    onClose: () => void;
    levelInfo: LevelInfo;
    avatarUrl?: string;
    onTitleChange: (title: UserTitle) => void;
    onFrameChange: (frame: AvatarFrameType) => void;
}

const ALL_TITLES: UserTitle[] = ['beginner', 'apprentice', 'scholar', 'expert', 'master', 'legend'];
const ALL_FRAMES: AvatarFrameType[] = ['none', 'bronze', 'silver', 'gold', 'platinum', 'diamond'];

export const CustomizationSheet: React.FC<CustomizationSheetProps> = ({
    isOpen,
    onClose,
    levelInfo,
    avatarUrl,
    onTitleChange,
    onFrameChange,
}) => {
    const { t } = useTranslation('progression');
    const [previewTitle, setPreviewTitle] = useState<UserTitle>(levelInfo.activeTitle);
    const [previewFrame, setPreviewFrame] = useState<AvatarFrameType>(levelInfo.activeFrame);
    const [saving, setSaving] = useState(false);
    const [activeTab, setActiveTab] = useState<'titles' | 'frames'>('titles');

    // Sync preview with actual when opening
    useEffect(() => {
        if (isOpen) {
            setPreviewTitle(levelInfo.activeTitle);
            setPreviewFrame(levelInfo.activeFrame);
        }
    }, [isOpen, levelInfo.activeTitle, levelInfo.activeFrame]);

    // Close on Escape
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape' && isOpen) onClose();
        };
        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [isOpen, onClose]);

    // Prevent body scroll when open
    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }
        return () => { document.body.style.overflow = ''; };
    }, [isOpen]);

    const handleSelectTitle = useCallback(async (title: UserTitle) => {
        if (!levelInfo.unlockedTitles.includes(title)) return;
        setPreviewTitle(title);
        if (title !== levelInfo.activeTitle) {
            try {
                setSaving(true);
                await progressionService.setActiveTitle(title);
                onTitleChange(title);
            } catch (err) {
                console.error('Failed to set title:', err);
                setPreviewTitle(levelInfo.activeTitle);
            } finally {
                setSaving(false);
            }
        }
    }, [levelInfo, onTitleChange]);

    const handleSelectFrame = useCallback(async (frame: AvatarFrameType) => {
        if (!levelInfo.unlockedFrames.includes(frame)) return;
        setPreviewFrame(frame);
        if (frame !== levelInfo.activeFrame) {
            try {
                setSaving(true);
                await progressionService.setActiveFrame(frame);
                onFrameChange(frame);
            } catch (err) {
                console.error('Failed to set frame:', err);
                setPreviewFrame(levelInfo.activeFrame);
            } finally {
                setSaving(false);
            }
        }
    }, [levelInfo, onFrameChange]);

    if (!isOpen) return null;

    return (
        <>
            {/* Backdrop */}
            <div className="customize-backdrop" onClick={onClose} aria-hidden="true" />

            {/* Sheet */}
            <div
                className="customize-sheet"
                role="dialog"
                aria-modal="true"
                aria-label={t('customize.title')}
            >
                {/* Drag handle (mobile) */}
                <div className="customize-drag-handle" />

                {/* Header */}
                <div className="customize-header">
                    <h3>{t('customize.title')}</h3>
                    <button className="customize-close" onClick={onClose} aria-label={t('customize.close')}>
                        <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                            <path d="M5 5L15 15M15 5L5 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                        </svg>
                    </button>
                </div>

                {/* Live Preview */}
                <div className="customize-preview">
                    <AvatarFrame
                        frame={previewFrame as FrameType}
                        avatarUrl={avatarUrl}
                        size={96}
                    />
                    <div className="customize-preview-badge">
                        <TitleBadge title={previewTitle as TitleType} size={36} />
                    </div>
                    <div className="customize-preview-label">
                        {t(`titles.${previewTitle}`)}
                        {previewFrame !== 'none' && ` · ${t(`frames.${previewFrame}`)}`}
                    </div>
                    {saving && <div className="customize-saving">{t('customize.saving')}</div>}
                </div>

                {/* Tabs */}
                <div className="customize-tabs" role="tablist">
                    <button
                        role="tab"
                        aria-selected={activeTab === 'titles'}
                        className={`customize-tab ${activeTab === 'titles' ? 'active' : ''}`}
                        onClick={() => setActiveTab('titles')}
                    >
                        {t('titles.sectionTitle')}
                    </button>
                    <button
                        role="tab"
                        aria-selected={activeTab === 'frames'}
                        className={`customize-tab ${activeTab === 'frames' ? 'active' : ''}`}
                        onClick={() => setActiveTab('frames')}
                    >
                        {t('frames.sectionTitle')}
                    </button>
                </div>

                {/* Content */}
                <div className="customize-content">
                    {activeTab === 'titles' && (
                        <div className="customize-grid" role="tabpanel">
                            {ALL_TITLES.map((title) => {
                                const isUnlocked = levelInfo.unlockedTitles.includes(title);
                                const isActive = previewTitle === title;
                                const unlockLevel = progressionService.getTitleUnlockLevel(title);

                                return (
                                    <button
                                        key={title}
                                        className={`customize-item ${isUnlocked ? 'unlocked' : 'locked'} ${isActive ? 'active' : ''}`}
                                        onClick={() => handleSelectTitle(title)}
                                        disabled={!isUnlocked || saving}
                                        aria-label={isUnlocked
                                            ? `${t(`titles.${title}`)} ${isActive ? `(${t('titles.current')})` : ''}`
                                            : `${t(`titles.${title}`)} - ${t('titles.locked', { level: unlockLevel })}`
                                        }
                                    >
                                        <TitleBadge title={title as TitleType} size={44} />
                                        <span className="customize-item-name">{t(`titles.${title}`)}</span>
                                        {!isUnlocked && (
                                            <span className="customize-item-lock">Lv.{unlockLevel}</span>
                                        )}
                                        {isActive && isUnlocked && (
                                            <span className="customize-item-check">&#10003;</span>
                                        )}
                                    </button>
                                );
                            })}
                        </div>
                    )}

                    {activeTab === 'frames' && (
                        <div className="customize-grid" role="tabpanel">
                            {ALL_FRAMES.map((frame) => {
                                const isUnlocked = levelInfo.unlockedFrames.includes(frame);
                                const isActive = previewFrame === frame;
                                const unlockLevel = progressionService.getFrameUnlockLevel(frame);

                                return (
                                    <button
                                        key={frame}
                                        className={`customize-item ${isUnlocked ? 'unlocked' : 'locked'} ${isActive ? 'active' : ''}`}
                                        onClick={() => handleSelectFrame(frame)}
                                        disabled={!isUnlocked || saving}
                                        aria-label={isUnlocked
                                            ? `${t(`frames.${frame}`)} ${isActive ? `(${t('frames.current')})` : ''}`
                                            : `${t(`frames.${frame}`)} - ${t('frames.locked', { level: unlockLevel })}`
                                        }
                                    >
                                        <AvatarFrame
                                            frame={frame as FrameType}
                                            avatarUrl={avatarUrl}
                                            size={44}
                                        />
                                        <span className="customize-item-name">{t(`frames.${frame}`)}</span>
                                        {!isUnlocked && (
                                            <span className="customize-item-lock">Lv.{unlockLevel}</span>
                                        )}
                                        {isActive && isUnlocked && (
                                            <span className="customize-item-check">&#10003;</span>
                                        )}
                                    </button>
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>
        </>
    );
};

export default CustomizationSheet;
